import React, { useState, useEffect, useRef } from "react";
import { Box, HStack, VStack, Text, Heading, Flex, Image, Input, Textarea, Button, Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
  IconButton,
  Tooltip,
  useToast
  } from "@chakra-ui/react";
import CorrectAnswerIcon from "../Images/CorrectAnswerIcon.svg"; 
import WrongAnswerIcon from "../Images/WrongAnswerIcon.svg"; 
import PartialAnswerIcon from "../Images/PartiallyCorrectAnswerIcon.svg"; 
import axios from "axios";
import AddIcon from "../Images/add-icon-dark.svg";
import { CloseIcon } from "@chakra-ui/icons";
import { BASE_API_URL } from './config';
import { a, PALETTES, useAppColorMode } from "./appColors";
import { solidButton, blueButton } from "./buttonStyles";
import { useAppToast } from "./appToast";
import { InlineMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import {
  HandThumbUpIcon as ThumbUpOutline,
  HandThumbDownIcon as ThumbDownOutline
} from "@heroicons/react/24/outline";

import {
  HandThumbUpIcon as ThumbUpSolid,
  HandThumbDownIcon as ThumbDownSolid
} from "@heroicons/react/24/solid";


const parseFeedback = (text, feedbackStatus) => {
  // A verdict can arrive without words — a mark and nothing written — so
  // this has to survive being handed nothing.
  let cleanedText = text || "";

  if (feedbackStatus === "PARTIALLY CORRECT") {
    cleanedText = cleanedText.replace(/\[\d+\s*marks?\]\.?\s*/gi, "").trim();
  }

  const parts = cleanedText.split(/(\$\$.*?\$\$|\$.*?\$|\\\(.*?\\\)|\\\[.*?\\\])/g);

  return parts.map((part, index) => {
    // $$ ... $$
    if (/^\$\$.*\$\$$/.test(part)) {
      const mathContent = part.slice(2, -2).trim();
      return <InlineMath key={index} math={mathContent} />;
    }
    // $ ... $
    if (/^\$.*\$$/.test(part)) {
      const mathContent = part.slice(1, -1).trim();
      return <InlineMath key={index} math={mathContent} />;
    }
    // \( ... \)
    if (/^\\\(.*\\\)$/.test(part)) {
      const mathContent = part.slice(2, -2).trim();
      return <InlineMath key={index} math={mathContent} />;
    }
    // \[ ... \]
    if (/^\\\[.*\\\]$/.test(part)) {
      const mathContent = part.slice(2, -2).trim();
      return <InlineMath key={index} math={mathContent} />;
    }

    // Plain text
    return <span key={index}>{part}</span>;
  });
}; 

const FeedBack = ({
  loading,
  questionAttemptGuid,
  feedbackData,
  totalMarks,
  userType,
  canEditFeedback = false,
  // Sized by the caller: on its own it sits under a question, inside an
  // answer card it has to fill the inset it was given.
  width = { base: "100%", md: "80%" },
  marginTop = "24px",
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isDark } = useAppColorMode();
  const toast = useAppToast(isDark);
  const [apiError, setApiError] = useState(null);
  const [newMark, setNewMark] = useState("");
  const [error, setError] = useState(null);
  const [updatedFeedback, setUpdatedFeedback] = useState(feedbackData);
  const [feedbackRating, setFeedbackRating] = useState(null); // null, 'positive', 'negative'
  // Teacher edit state (only used when canEditFeedback): editable feedback text
  // + mark saved back to the question attempt during review.
  const [editText, setEditText] = useState("");
  const [editMark, setEditMark] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const feedbackRef = useRef(null);

  // Auto-grow the feedback textarea so it never shows an inner scrollbar.
  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    autoGrow(feedbackRef.current);
  }, [editText]);


  const { sampleImage, feedback, feedbackStatus, mark } = updatedFeedback;

  const p = isDark ? PALETTES.dark : PALETTES.light; // literal palette for the portalled modal
  const [bgColor, setBgColor] = useState(a.okBg);
  const token = localStorage.getItem("accessToken");

  useEffect(() => {
    setUpdatedFeedback(feedbackData);
  }, [feedbackData]);

  useEffect(() => {
    setUpdatedFeedback(feedbackData);
  }, [feedbackData]);

  useEffect(() => {
    setUpdatedFeedback(feedbackData);

    // Handle API error in feedbackData
    if (feedbackData?.detail) {
      setApiError(feedbackData.detail); // Set error message
      onOpen(); // Open modal
    }
  }, [feedbackData, onOpen]);

  // Seed the teacher's editable fields from the loaded feedback.
  useEffect(() => {
    setEditText(feedbackData?.feedback || "");
    setEditMark(
      feedbackData?.mark === null || feedbackData?.mark === undefined
        ? ""
        : String(feedbackData.mark)
    );
  }, [feedbackData]);

  useEffect(() => {
    switch (feedbackStatus) {
      case "CORRECT":
        setBgColor(a.okBg);
        break;
      case "PARTIALLY CORRECT":
        setBgColor(a.warnBg);
        break;
      case "INCORRECT":
        setBgColor(a.badBg);
        break;
      default:
        setBgColor(a.surface);
    }
  }, [feedbackStatus]); 
  
  const handleUpgradeClick = async () => {
    const token = `Bearer ${localStorage.getItem("accessToken")}`;

    try {
      const checkoutResponse = await fetch(
        `${BASE_API_URL}/api/v1/user-subscription-plans/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            subscription_plan_guid: "298eb053-7c3a-45d6-967f-f73508517aa9",
            stripe_price_id: "price_1QkqC7C7LkUNUnN8V3nYcQd8",
            renewal_type: "MONTHLY",
          }),
        }
      );

      if (!checkoutResponse.ok) {
        throw new Error("Failed to create checkout session");
      }

      const checkoutData = await checkoutResponse.json();
      const checkoutUrl = checkoutData.checkout_url;

      window.location.href = checkoutUrl;
    } catch (err) {
      console.error("Error creating checkout session:", err.message);
    }
  };

  
  const handleSubmit = async () => {
    try {
      const requestBody = {
        feedback_mark: parseInt(newMark, 10),
        completion_status: "COMPLETE",
      };

      const response = await axios.put(
        `${BASE_API_URL}/api/v1/user-question-attempts/${questionAttemptGuid}`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUpdatedFeedback((prevFeedback) => ({
        ...prevFeedback,
        feedbackStatus: response.data.feedback_correctness,
        mark: response.data.feedback_mark,
      }));

      setError(null);
    } catch (err) {
      setError("There was an error submitting your mark.");
      console.error(err);
    }
  };

  
  const handleMarkChange = (e) => {
    setNewMark(e.target.value);
  };

  // Teacher review: save edited feedback text + mark to the question attempt.
  const handleSaveTeacherFeedback = async () => {
    if (!questionAttemptGuid || savingFeedback) return;
    setSavingFeedback(true);
    try {
      const body = { feedback_text: editText, completion_status: "COMPLETE" };
      const parsed = parseInt(editMark, 10);
      if (editMark !== "" && !Number.isNaN(parsed)) body.feedback_mark = parsed;
      const { data } = await axios.put(
        `${BASE_API_URL}/api/v1/user-question-attempts/${questionAttemptGuid}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUpdatedFeedback((prev) => ({
        ...prev,
        feedback: data.feedback_text,
        mark: data.feedback_mark,
        feedbackStatus: data.feedback_correctness,
      }));
      toast({ title: "Feedback saved", status: "success", duration: 1500 });
    } catch (err) {
      console.error("Failed to save feedback:", err);
      toast({ title: "Could not save feedback", description: "Please try again.", status: "error", duration: 2500 });
    } finally {
      setSavingFeedback(false);
    }
  };

  if (loading) {
    return true;
  }
  
  if (apiError) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg={p.surface} color={p.text} borderRadius="12px" padding="20px">
          <Flex justifyContent="flex-end">
            <IconButton
              icon={<CloseIcon />}
              onClick={onClose}
              variant="ghost"
              aria-label="Close modal"
              size="sm"
            />
          </Flex>
          <ModalHeader textAlign="center" fontSize="18px" fontWeight="600">
            {apiError}
          </ModalHeader>
          <ModalBody>
            <Text textAlign="center" fontSize="14px" mb="4px">
              Upgrade to premium to access this feature and get more detailed feedback.
            </Text>
            <Flex justifyContent="center">
              <Button
                marginTop = " 24px "
                variant="unstyled"
                borderRadius="14px"
                {...blueButton}
                width="180px"
                onClick={handleUpgradeClick}
              >
                <Flex padding="14px" gap="4px" display="inline-flex">
                  <Heading fontSize="12px" fontWeight="400">Upgrade to premium</Heading>
                  <Image src={AddIcon} alt="upgrade-icon" boxSize = "14px " marginBottom = " 34px " />
                </Flex>
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  
    const handleFeedbackRating = async (rating) => {
      const newRating = feedbackRating === rating ? "NULL" : rating;
      setFeedbackRating(newRating);
      console.log(newRating)
      try {
        const requestBody = {
          feedback_rating: newRating
        };
  
        await axios.put(
          `${BASE_API_URL}/api/v1/user-question-attempts/set-feedback-rating/${questionAttemptGuid}`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`, 
            },
          }
        );
      } catch (err) {
        console.error(err);
      }
    };

  return (
    <>
      <Box width={width} padding="24px" bg={bgColor} borderRadius="14px" marginTop={marginTop} position="relative" border="1px solid" borderColor={a.border}>
        <HStack justify="space-between">
          <Flex display="inline-flex" gap="4px" marginBottom="14px">
            <Image
              src={
                feedbackStatus === "CORRECT"
                  ? CorrectAnswerIcon
                  : feedbackStatus === "PARTIALLY CORRECT"
                  ? PartialAnswerIcon
                  : WrongAnswerIcon
              }
              alt={`${feedbackStatus}-icon`}
            />
            <Heading color={a.text} fontSize="14px" fontWeight="500">
              {feedbackStatus}
            </Heading>
          </Flex>
          <Flex>
            {feedbackStatus !== "PARTIALLY CORRECT" && (
              <Heading color={a.text} fontSize="12px" fontWeight="300">
                {`${mark} marks / ${totalMarks}`}
              </Heading>
            )}
          </Flex>
        </HStack>

      <VStack spacing={1} alignItems="left">
        <Heading color={a.text} fontSize="14px" fontWeight="500">
          Feedback
        </Heading>

        <Box position="relative" width="100%">
          {/* Teacher review: editable feedback + mark. Otherwise read-only. */}
          {canEditFeedback ? (
            <VStack align="stretch" spacing="10px">
              <Textarea
                ref={feedbackRef}
                value={editText}
                onChange={(e) => { setEditText(e.target.value); autoGrow(e.target); }}
                placeholder="Write feedback and notes for the student…"
                fontSize="14px"
                color={a.text}
                borderRadius="12px"
                border="1px solid"
                borderColor={a.border}
                bg={a.inputBg}
                minH="120px"
                overflowY="hidden"
                resize="none"
              />
              <HStack spacing="10px" align="center">
                <Text fontSize="13px" color={a.text} fontWeight="400">Mark</Text>
                <Input
                  type="number"
                  value={editMark}
                  onChange={(e) => setEditMark(e.target.value)}
                  placeholder="0"
                  width="90px"
                  borderRadius="10px"
                  border="1px solid"
                  borderColor={a.border}
                  bg={a.inputBg}
                />
                <Text fontSize="13px" color={a.textSub}>/ {totalMarks}</Text>
                <Button
                  onClick={handleSaveTeacherFeedback}
                  isLoading={savingFeedback}
                  loadingText="Saving"
                  {...solidButton}
                  fontSize="13px"
                  fontWeight="400"
                  borderRadius="10px"
                >
                  Save feedback
                </Button>
              </HStack>
            </VStack>
          ) : (
            <Text color={a.text} fontSize="14px" fontWeight="250">
              {parseFeedback(feedback, feedbackStatus)}
            </Text>
          )}

            {sampleImage && sampleImage !== "no image url" && (
              <>
                <Heading color={a.text} fontSize="12px" fontWeight="400">
                  This type of question might not be answered correctly by AI. Please mark yourself based on the sample answer image below:
                </Heading>
                <Image src={sampleImage} alt="sample-image" borderRadius="12px" maxW="100%" mt="8px" />
                <Input
                  type="number"
                  placeholder="Please enter your new mark here"
                  value={newMark}
                  onChange={handleMarkChange}
                  borderRadius="14px"
                  border="0.5px solid" borderColor={a.inputBorder}
                  mt="12px"
                />
                <Button
                  onClick={handleSubmit}
                  {...solidButton}
                  fontSize="14px"
                  fontWeight="400"
                  mt="14px"
                  borderRadius="14px"
                >
                  Submit Mark
                </Button>
                {error && <Text color="red.500">{error}</Text>}
              </> 
            )}
          
      
          </Box>
        </VStack>
      </Box>
      {questionAttemptGuid && (
      <Box>
        <HStack spacing={3} mt="8px" ml="24px">
          <Tooltip label="Good response" fontSize="11px" borderRadius="4px">
            <Box cursor="pointer" onClick={() => handleFeedbackRating("POSITIVE")}>
              {feedbackRating === "POSITIVE"
                ? <ThumbUpSolid width={20} height={20} />
                : <ThumbUpOutline width={20} height={20} />}
            </Box>
          </Tooltip>
          <Tooltip label="Bad response" fontSize="11px" borderRadius="4px">
            <Box cursor="pointer" onClick={() => handleFeedbackRating("NEGATIVE")}>
              {feedbackRating === "NEGATIVE"
                ? <ThumbDownSolid width={20} height={20} />
                : <ThumbDownOutline width={20} height={20} />}
            </Box>
          </Tooltip>
        </HStack>
      </Box>
    )}

    </>
  );
};

export default FeedBack;
